import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PurchaseCostTypeRecord } from "@/lib/catalog/purchaseCostTypesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Tag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type PurchaseCostTypeSelectProps = {
  value: string;
  types: PurchaseCostTypeRecord[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  takenTypeIds?: string[] | Set<string>;
};

function itemValue(id: string): string {
  return `cost_type:${id}`;
}

export function PurchaseCostTypeSelect({
  value,
  types,
  onChange,
  placeholder = "Select cost type",
  triggerClassName,
  disabled = false,
  takenTypeIds,
}: PurchaseCostTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState("");

  const takenSet = useMemo(() => {
    if (!takenTypeIds) return new Set<string>();
    if (takenTypeIds instanceof Set) return takenTypeIds;
    return new Set(takenTypeIds);
  }, [takenTypeIds]);

  const selected = useMemo(
    () => types.find((t) => String(t.id) === String(value)) ?? null,
    [types, value],
  );

  // Group chart_of_accounts leaves by account_detail_types (categoryName)
  const groups = useMemo(() => {
    const byParent = new Map<string, PurchaseCostTypeRecord[]>();
    for (const item of types) {
      const parent = (item.categoryName ?? "").trim() || "General Cost Types";
      const list = byParent.get(parent) ?? [];
      list.push(item);
      byParent.set(parent, list);
    }
    return Array.from(byParent.entries()).map(([parent, items]) => ({
      parent,
      items: [...items].sort(
        (a, b) =>
          a.code.localeCompare(b.code, undefined, { numeric: true }) ||
          (a.label ?? "").localeCompare(b.label ?? ""),
      ),
    }));
  }, [types]);

  useEffect(() => {
    if (!open) return;
    setHighlight(value ? itemValue(value) : "");
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-cost-type-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-11 w-full justify-between px-3.5 font-normal text-sm transition-all hover:bg-muted/30 hover:border-primary/40",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <Tag className="h-3.5 w-3.5 shrink-0 opacity-60 text-primary" />
            <span className="truncate">
              {selected ? (
                <>
                  <span className="font-semibold text-foreground">{selected.label}</span>
                  {selected.categoryName ? (
                    <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                      ({selected.categoryName})
                    </span>
                  ) : null}
                </>
              ) : (
                placeholder
              )}
            </span>
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0 z-[100] shadow-xl border-border/60" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search cost type, category or code…" className="h-10 text-xs border-none focus:ring-0" />
          <CommandList className="max-h-[260px] overflow-y-auto p-1">
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
              No cost type found.
            </CommandEmpty>
            {groups.map((group) => (
              <CommandGroup
                key={group.parent}
                heading={group.parent}
                className="[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-primary/80 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
              >
                {group.items.map((t) => {
                  const isSelected = String(t.id) === String(value);
                  const isTaken = !isSelected && takenSet.has(String(t.id));

                  return (
                    <CommandItem
                      key={t.id}
                      value={itemValue(t.id)}
                      disabled={isTaken}
                      keywords={[t.label ?? "", t.code ?? "", t.categoryName ?? ""]}
                      data-cost-type-selected={isSelected ? "true" : undefined}
                      onSelect={() => {
                        if (isTaken) return;
                        onChange(t.id);
                        setOpen(false);
                      }}
                      className={cn(
                        "text-xs flex items-center justify-between cursor-pointer rounded-md px-2.5 py-2 my-0.5 transition-colors",
                        isSelected && "bg-primary/10 font-semibold text-primary",
                        isTaken && "opacity-40 cursor-not-allowed",
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0 pr-2">
                        <span className="truncate font-medium">{t.label}</span>
                        {t.code ? (
                          <span className="font-mono text-[10px] opacity-70 bg-muted/60 px-1.5 py-0.5 rounded border border-border/30 shrink-0">
                            #{t.code}
                          </span>
                        ) : null}
                        {isTaken ? (
                          <span className="text-[10px] font-normal text-muted-foreground italic shrink-0">
                            (Added)
                          </span>
                        ) : null}
                      </div>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
